import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { getLocalStorageItem, errorToast, successToast } from '../../utils/helper';
import Topbar from '../PulseSurvey/Topbar';
import Loader from '../../component/common/Loader';
import { CompanyApi } from '../../api/companyApi';
import Table1 from '../../component/common/Table1';
import Pagination from '../../component/common/Pagination/Pagination';
import SearchInput from '../../component/common/Input/SearchInput';
import { PER_PAGE } from '../../utils/constants';
import ConfirmPopup from '../../component/common/modals/ConfirmPopup';
import CompanyAddUsersModal from './CompanyAddUsersModal';
import SchemeLandingBanner from '../../component/common/SchemeLandingBanner';
import ShoorahHealthSchemesList from './ShoorahHealthSchemesList';
import imgHealthcare from '../../assets/images/instantsupport-headphones.png';

const ENROLLMENT_STATUS = {
  1: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  },
  2: {
    label: 'Active',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  },
  3: {
    label: 'Suspended',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
  },
  4: {
    label: 'Cancelled',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  },
  5: {
    label: 'Completed',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
  }
};

const STATUS_FILTER = [
  { value: '', name: 'All Status' },
  { value: '1', name: 'Pending' },
  { value: '2', name: 'Active' },
  { value: '3', name: 'Suspended' },
  { value: '4', name: 'Cancelled' },
  { value: '5', name: 'Completed' }
];

const columns = [
  {
    title: 'Employee',
    key: 'userName',
    parent: 'element',
    sortable: false,
    type: 'text',
    align: 'left'
  },
  {
    title: 'Email',
    key: 'userEmail',
    parent: 'element',
    sortable: false,
    type: 'text',
    align: 'left'
  },
  {
    title: 'Scheme',
    key: 'schemeName',
    parent: 'element',
    sortable: false,
    type: 'text',
    align: 'left'
  },
  {
    title: 'Enrolled',
    key: 'enrolledDate',
    parent: 'element',
    sortable: false,
    type: 'date',
    align: 'left'
  }
];

const HealthSchemePage = () => {
  const navigate = useNavigate();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const [loading, setLoading] = useState(false);
  const [schemeData, setSchemeData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [showShoorahSchemes, setShowShoorahSchemes] = useState(false);
  const [shoorahSchemes, setShoorahSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);

  // Table states
  const [enrollments, setEnrollments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(STATUS_FILTER[0]);

  // Fetch scheme data on component mount
  useEffect(() => {
    const fetchSchemeData = async () => {
      if (!userData?.companyId) return;

      setLoading(true);
      try {
        const response = await CompanyApi.getHealthScheme(userData.companyId);

        if (response?.data?.meta?.code === 1 && response?.data?.data) {
          setSchemeData(response.data.data || null);
        }
      } catch (error) {
        console.error('Error fetching health scheme:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchemeData();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Fetch enrollments
  useEffect(() => {
    if (schemeData) {
      fetchEnrollments(currentPage, selectedPerPage.value, searchTerm, selectedStatus.value);
    }
  }, [currentPage, selectedPerPage, schemeData]);

  // Debounced search
  useEffect(() => {
    if (searchTerm !== undefined && schemeData) {
      const delayDebounceFn = setTimeout(() => {
        fetchEnrollments(1, selectedPerPage.value, searchTerm, selectedStatus.value);
        setCurrentPage(1);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  // Status filter
  useEffect(() => {
    if (schemeData) {
      fetchEnrollments(1, selectedPerPage.value, searchTerm, selectedStatus.value);
      setCurrentPage(1);
    }
  }, [selectedStatus]);

  const fetchEnrollments = async (page, limit, search, status) => {
    setLoading(true);
    try {
      // TODO: Replace with actual health scheme enrollments API when available
      const response = await CompanyApi.getHealthSchemeEnrollments(page, limit, search, status);

      if (response?.data?.meta?.code === 1) {
        const formattedEnrollments = (response?.data?.data || []).map((enrollment) => ({
          ...enrollment,
          element: {
            ...enrollment,
            userName: enrollment.name || 'N/A',
            userEmail: enrollment.email || 'N/A',
            schemeName: enrollment.schemeId?.providerName || 'Shoorah Health',
            enrolledDate: enrollment.enrolledDate
          }
        }));
        setEnrollments(formattedEnrollments);
        setTotalCount(response?.data?.totalRecords || 0);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch enrollments');
        setEnrollments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      errorToast('Failed to fetch enrollments');
      setEnrollments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewScheme = async () => {
    setLoadingSchemes(true);
    try {
      const response = await CompanyApi.getShoorahHealthScheme();

      if (response?.data?.meta?.code === 1 && response?.data?.data) {
        setShoorahSchemes(response.data.data);
        setShowShoorahSchemes(true);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch Shoorah health schemes');
      }
    } catch (error) {
      console.error('Error fetching Shoorah health schemes:', error);
      errorToast('Failed to fetch Shoorah health schemes');
    } finally {
      setLoadingSchemes(false);
    }
  };

  const handleCreateCustomScheme = () => {
    navigate(`/${userData.slug}/health-scheme/scheme`);
  };

  const handleSelectScheme = (scheme) => {
    console.log('Selected health scheme:', scheme);
    navigate(`/${userData.slug}/health-scheme/scheme`, {
      state: { selectedScheme: scheme }
    });
  };

  const handleBackToLanding = () => {
    setShowShoorahSchemes(false);
  };

  const handleAddUsers = () => {
    setShowAddUsersModal(true);
  };

  const handleModalClose = () => {
    setShowAddUsersModal(false);
  };

  const handleModalSuccess = () => {
    setShowAddUsersModal(false);
    // Refresh enrollments table
    fetchEnrollments(currentPage, selectedPerPage.value, searchTerm, selectedStatus.value);
  };

  const handleEditPlan = () => {
    setShowMenuDropdown(false);
    navigate(`/${userData.slug}/health-scheme/scheme`);
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    setCurrentPage(1);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const refreshTable = () => {
    fetchEnrollments(currentPage, selectedPerPage.value, searchTerm, selectedStatus.value);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const confirmDelete = async () => {
    if (!schemeData?._id) return;

    setIsDeleting(true);
    try {
      const response = await CompanyApi.deleteHealthScheme(schemeData._id);

      if (response?.data?.meta?.code === 1) {
        successToast('Health scheme deleted successfully');
        setShowDeleteConfirm(false);
        // Refresh the page data
        setSchemeData(null);
        setEnrollments([]);
        setTotalCount(0);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to delete scheme');
      }
    } catch (error) {
      console.error('Error deleting scheme:', error);
      errorToast('Failed to delete scheme');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        {loading && <Loader />}
        <Helmet>
          <meta charSet="utf-8" />
          <title>B2B | Health Scheme</title>
        </Helmet>

        <Topbar title={'Health Scheme'} infoBtn={false} />

        {!schemeData ? (
          // Landing Banner - shown when company has no scheme
          <div className="mt-8">
            <SchemeLandingBanner
              title="Health Scheme"
              description="Empower your employees with comprehensive health and wellness benefits. Choose from our pre-configured health plans or create a custom scheme tailored to your company's needs."
              primaryButtonText="View Plans"
              primaryButtonAction={handleViewScheme}
              secondaryButtonText="Add Custom Plan"
              secondaryButtonAction={handleCreateCustomScheme}
              imageSrc={imgHealthcare}
              imageAlt="Health Scheme"
            />

            {showShoorahSchemes && (
              <ShoorahHealthSchemesList
                shoorahSchemes={shoorahSchemes}
                loadingSchemes={loadingSchemes}
                onBack={handleBackToLanding}
              />
            )}
          </div>
        ) : null}

        {/* Plan Info Card */}
        {schemeData && (
          <div className="xl:px-8 lg:px-6 px-4 py-[10px] sm:mt-6 mt-4 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border border-gray-200 rounded-lg flex sm:justify-between justify-center mb-0 items-center">
            <div className="font-medium text-sm flex items-center">
              <span>
                Plan {'  '} - {'  '}
                <span className="text-[1.2rem] font-bold">
                  {schemeData.schemeType == 2 ? 'Custom Plan' : 'Shoorah Plan'}
                </span>
              </span>
            </div>

            <div className="flex flex-row sm:flex-nowrap items-center gap-2">
              {schemeData.schemeType == 2 && (
                <div className="relative">
                  <button
                    onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                    className="h-[32px] px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center"
                  >
                    <svg
                      className="w-6 h-6 text-gray-600 dark:text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" />
                      <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" />
                      <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showMenuDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50">
                      <div className="py-1">
                        <button
                          onClick={handleEditPlan}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit Plan
                        </button>
                        <button
                          onClick={() => {
                            setShowMenuDropdown(false);
                            handleDelete();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 border-t border-gray-200 dark:border-gray-600"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete Plan
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {schemeData && (
          <div className="mt-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Enrolled Users
              </h2>
            </div>

            {/* Table */}
            <div className="bg-white p-3 dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="flex pb-2 flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[200px]">
                  <SearchInput
                    id="searchKey"
                    name="searchKey"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by employee name or email..."
                  />
                </div>
                <button
                  onClick={handleAddUsers}
                  className="btn-primary"
                >
                  {'  '}
                  Manage Users &nbsp;
                  <svg
                    className="w-5 h-5 text-white-600 dark:text-white-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </button>
              </div>

              <Table1
                columns={columns}
                data={enrollments}
                name={'health_scheme_enrollments'}
                bottomBorder={totalCount > selectedPerPage.value}
                loader={loading}
                refreshTable={refreshTable}
                contentType={1}
              />
            </div>

            {/* Pagination */}
            <div className="mt-4">
              {enrollments.length > 0 && !loading && totalCount > selectedPerPage.value ? (
                <Pagination
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={selectedPerPage?.value}
                  onPageChange={handlePagination}
                />
              ) : null}
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        <ConfirmPopup
          open={showDeleteConfirm}
          setOpen={setShowDeleteConfirm}
          setAccepted={confirmDelete}
          handleNo={handleDeleteCancel}
          message="Are you sure you want to delete this health scheme? This action cannot be undone and will remove all associated data."
          isLoading={isDeleting}
        />

        {/* Add Users Modal */}
        <CompanyAddUsersModal
          isOpen={showAddUsersModal}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      </div>
    </>
  );
};

export default HealthSchemePage;
